import React, { useEffect, useRef, useState } from "react";
import "../Style/Achievements.css";
import { useLanguage } from "../Context/Languagecontext";

/* ── Card Component ──────────────────────────────────────── */
function AchievementCard({ item, index }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.12 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`ach-card${visible ? " visible" : ""}`}
      style={{ transitionDelay: `${index * 0.12}s` }}
    >
      <div className="ach-card-left">
        <div className="ach-card-icon">{item.icon}</div>
        <div className="ach-card-line" />
      </div>
      <div className="ach-card-body">
        <div className="ach-card-meta">
          <span className="ach-card-category">{item.category}</span>
          <span className="ach-card-highlight">{item.highlight}</span>
        </div>
        <h3 className="ach-card-title">{item.title}</h3>
        <p className="ach-card-tagline">{item.tagline}</p>
        <p className="ach-card-desc">{item.desc}</p>
      </div>
    </div>
  );
}

/* ── Main Component ──────────────────────────────────────── */
export default function Achievements() {
  const sectionRef = useRef(null);
  const trophyRef = useRef(null);

  const { t, lang } = useLanguage();
  const ach = t.achievements;

  const [headerVisible, setHeaderVisible] = useState(false);
  const [trophyVisible, setTrophyVisible] = useState(false);
  const [orbitVisible, setOrbitVisible] = useState(false);

  useEffect(() => {
    const obs1 = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setHeaderVisible(true);
      },
      { threshold: 0.1 },
    );
    const obs2 = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setTrophyVisible(true);
          setTimeout(() => setOrbitVisible(true), 700);
        }
      },
      { threshold: 0.2 },
    );
    if (sectionRef.current) obs1.observe(sectionRef.current);
    if (trophyRef.current) obs2.observe(trophyRef.current);
    return () => {
      obs1.disconnect();
      obs2.disconnect();
    };
  }, []);

  /* ── Localized Structural Array Mapping ────────────────── */
  const achievementsList = [
    {
      id: 1,
      category: ach?.cards?.card1?.category || "",
      tagline: ach?.cards?.card1?.tagline || "",
      title: ach?.cards?.card1?.title || "",
      desc: ach?.cards?.card1?.desc || "",
      highlight: ach?.cards?.card1?.highlight || "",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
          <path d="M4 22h16" />
          <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
          <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
          <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
        </svg>
      ),
    },
    {
      id: 2,
      category: ach?.cards?.card2?.category || "",
      tagline: ach?.cards?.card2?.tagline || "",
      title: ach?.cards?.card2?.title || "",
      desc: ach?.cards?.card2?.desc || "",
      highlight: ach?.cards?.card2?.highlight || "",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
      ),
    },
    {
      id: 3,
      category: ach?.cards?.card3?.category || "",
      tagline: ach?.cards?.card3?.tagline || "",
      title: ach?.cards?.card3?.title || "",
      desc: ach?.cards?.card3?.desc || "",
      highlight: ach?.cards?.card3?.highlight || "",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
    },
    {
      id: 4,
      category: ach?.cards?.card4?.category || "",
      tagline: ach?.cards?.card4?.tagline || "",
      title: ach?.cards?.card4?.title || "",
      desc: ach?.cards?.card4?.desc || "",
      highlight: ach?.cards?.card4?.highlight || "",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
    {
      id: 5,
      category: ach?.cards?.card5?.category || "",
      tagline: ach?.cards?.card5?.tagline || "",
      title: ach?.cards?.card5?.title || "",
      desc: ach?.cards?.card5?.desc || "",
      highlight: ach?.cards?.card5?.highlight || "",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ),
    },
    {
      id: 6,
      category: ach?.cards?.card6?.category || "",
      tagline: ach?.cards?.card6?.tagline || "",
      title: ach?.cards?.card6?.title || "",
      desc: ach?.cards?.card6?.desc || "",
      highlight: ach?.cards?.card6?.highlight || "",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      ),
    },
  ];

  /* ── Orbit Icons Localized Setup ───────────────────────── */
  const orbitIcons = [
    {
      label: ach?.orbitLabels?.legacy || "Legacy",
      svg: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
          <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
        </svg>
      ),
    },
    {
      label: ach?.orbitLabels?.growth || "Growth",
      svg: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
      ),
    },
    {
      label: ach?.orbitLabels?.nation || "Nation",
      svg: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
    },
    {
      label: ach?.orbitLabels?.people || "People",
      svg: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
    {
      label: ach?.orbitLabels?.world || "World",
      svg: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      ),
    },
    {
      label: ach?.orbitLabels?.honor || "Honor",
      svg: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ),
    },
  ];

  const leftCards = achievementsList.slice(0, 3);
  const rightCards = achievementsList.slice(3, 6);

  return (
    <section
      className={`ach-section${lang === "ta" ? " ach-section--tamil" : ""}`}
      id="achievements"
      ref={sectionRef}
    >
      {/* Background shimmer lines */}
      <div className="ach-bg-lines" aria-hidden="true">
        {[...Array(5)].map((_, i) => (
          <span
            key={i}
            className="ach-bg-line"
            style={{ animationDelay: `${i * 1.2}s` }}
          />
        ))}
      </div>

      <div className="ach-inner">
        {/* ── Header ── */}
        <header className={`ach-header${headerVisible ? " visible" : ""}`}>
          <span className="ach-eyebrow">{ach?.eyebrow}</span>
          <h2 className="ach-title">
            {ach?.titleNormal}
            <span>{ach?.titleAccent}</span>
          </h2>
          <p className="ach-subtitle">{ach?.subtitle}</p>
        </header>

        {/* ── Body: Left cards | Trophy | Right cards ── */}
        <div className="ach-body">
          {/* Left cards */}
          <div className="ach-cards-left">
            {leftCards.map((item, i) => (
              <AchievementCard key={item.id} item={item} index={i} />
            ))}
          </div>

          {/* Trophy (centre) */}
          <div className="ach-trophy-col">
            <div className="ach-trophy-stage" ref={trophyRef}>
              {/* Rings */}
              <div className="ach-ring ach-ring--outer" />
              <div className="ach-ring ach-ring--inner" />

              {/* Orbit icons */}
              {orbitIcons.map((ic, i) => (
                <div
                  key={i}
                  className={`ach-orbit-icon${orbitVisible ? " visible" : ""}`}
                  data-label={ic.label}
                  style={{ "--orbit-delay": `${i * 0.08}s` }}
                >
                  {ic.svg}
                </div>
              ))}

              {/* Trophy SVG */}
              <div
                className={`ach-trophy-wrap${trophyVisible ? " visible" : ""}`}
              >
                <div className="ach-glow" />
                <svg
                  className="ach-trophy-svg"
                  viewBox="0 0 200 240"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="60"
                    y="210"
                    width="80"
                    height="14"
                    rx="7"
                    fill="#C9942A"
                  />
                  <rect
                    x="50"
                    y="222"
                    width="100"
                    height="10"
                    rx="5"
                    fill="#A37820"
                  />
                  <rect
                    x="88"
                    y="190"
                    width="24"
                    height="22"
                    rx="4"
                    fill="#C9942A"
                  />
                  <rect
                    x="92"
                    y="190"
                    width="16"
                    height="22"
                    rx="3"
                    fill="#F4C764"
                  />
                  <path
                    d="M55 60 Q50 130 90 190 H110 Q150 130 145 60 Z"
                    fill="url(#tG)"
                  />
                  <path
                    d="M80 60 Q76 120 100 185 Q124 120 120 60 Z"
                    fill="url(#tS)"
                  />
                  <rect
                    x="50"
                    y="52"
                    width="100"
                    height="14"
                    rx="7"
                    fill="url(#tR)"
                  />
                  <path
                    d="M55 75 Q28 80 28 105 Q28 130 55 135"
                    stroke="url(#tH)"
                    strokeWidth="10"
                    fill="none"
                    strokeLinecap="round"
                  />
                  <path
                    d="M145 75 Q172 80 172 105 Q172 130 145 135"
                    stroke="url(#tH)"
                    strokeWidth="10"
                    fill="none"
                    strokeLinecap="round"
                  />
                  <polygon
                    points="100,95 104,108 118,108 107,116 111,129 100,121 89,129 93,116 82,108 96,108"
                    fill="rgba(54,4,5,0.35)"
                  />
                  <polygon
                    points="100,92 104.5,106 119,106 108,115 112,129 100,120 88,129 92,115 81,106 95.5,106"
                    fill="#C9942A"
                  />
                  <defs>
                    <linearGradient
                      id="tG"
                      x1="55"
                      y1="60"
                      x2="145"
                      y2="190"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0%" stopColor="#F9DB96" />
                      <stop offset="50%" stopColor="#F4C764" />
                      <stop offset="100%" stopColor="#C9942A" />
                    </linearGradient>
                    <linearGradient
                      id="tS"
                      x1="80"
                      y1="60"
                      x2="120"
                      y2="190"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0%" stopColor="rgba(255,255,255,0.3)" />
                      <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                    </linearGradient>
                    <linearGradient
                      id="tR"
                      x1="50"
                      y1="0"
                      x2="150"
                      y2="0"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0%" stopColor="#C9942A" />
                      <stop offset="50%" stopColor="#F9DB96" />
                      <stop offset="100%" stopColor="#C9942A" />
                    </linearGradient>
                    <linearGradient
                      id="tH"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                      gradientUnits="objectBoundingBox"
                    >
                      <stop offset="0%" stopColor="#F9DB96" />
                      <stop offset="100%" stopColor="#C9942A" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>

            {/* Name card below trophy */}
            <div className={`ach-namecard${trophyVisible ? " visible" : ""}`}>
              <div className="ach-namecard-name">{ach?.namecard?.name}</div>
              <div className="ach-namecard-role">{ach?.namecard?.role}</div>
            </div>
          </div>

          {/* Right cards */}
          <div className="ach-cards-right">
            {rightCards.map((item, i) => (
              <AchievementCard key={item.id} item={item} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
